import { useLayoutEffect, useRef, useState, type FC } from 'react';
import { cn } from '@/lib/utils';

/**
 * Renders single-line text that auto-scrolls horizontally (marquee) when it
 * overflows its container, instead of being truncated with an ellipsis. When the
 * text fits, it renders normally (truncate as a safety fallback).
 *
 * `className` is applied to the text so callers keep their font/color styling.
 */
export const AutoScrollText: FC<{ text?: string; className?: string }> = ({ text = '', className }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  // null = fits (no scroll); number = animation duration in seconds
  const [duration, setDuration] = useState<number | null>(null);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const measure = measureRef.current;
    if (!wrap || !measure) return;
    const overflow = measure.scrollWidth - wrap.clientWidth;
    if (overflow > 1) {
      // ~40px/sec, min 4s, so longer names scroll proportionally (not too fast)
      setDuration(Math.max(4, measure.scrollWidth / 40));
    } else {
      setDuration(null);
    }
  }, [text]);

  return (
    <div ref={wrapRef} className="relative w-full overflow-hidden whitespace-nowrap">
      {/* invisible full-width measurer (always present so text changes re-measure) */}
      <span ref={measureRef} aria-hidden className={cn('invisible absolute left-0 top-0 whitespace-nowrap', className)}>
        {text}
      </span>
      {duration ? (
        <div className="marquee-track" style={{ animationDuration: `${duration}s` }}>
          <span className={cn('inline-block pr-10', className)}>{text}</span>
          <span className={cn('inline-block pr-10', className)} aria-hidden>{text}</span>
        </div>
      ) : (
        <span className={cn('block max-w-full truncate', className)}>{text}</span>
      )}
    </div>
  );
};
